/**
 * Searchable OpenRouter model picker.
 *
 * Wraps MUI Autocomplete so every eval/evolve dropdown gets:
 *   - Type-to-filter across the full OpenRouter catalogue.
 *   - Inline prices ($/1M input · $/1M output) on each option.
 *   - Multi-select mode for the matrix model chips.
 *
 * Keep it dumb: accepts the model list as a prop so the parent stays in
 * control of loading state, and renders a TextField pass-through so
 * existing form layouts don't need a rewrite.
 */
import React from 'react';
import {Autocomplete, Box, Chip, TextField, Tooltip, Typography} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type {OpenRouterModelEntry} from '../../types/generated/graphql';
import {pcolors} from '../../theme/planapusTheme';

function formatPrice(v: number | null | undefined): string {
  if (v == null) return '—';
  // OpenRouter quotes in $/1M. Keep precision for fractional-cent models.
  if (v < 1) return `$${v.toFixed(2)}`;
  if (v < 10) return `$${v.toFixed(2)}`;
  return `$${v.toFixed(1)}`;
}

function priceLabel(m: OpenRouterModelEntry): string {
  return `${formatPrice(m.inputPer1M)} / ${formatPrice(m.outputPer1M)}`;
}

function ModelOption({model}: {model: OpenRouterModelEntry}) {
  return (
    <Box sx={{display: 'flex', alignItems: 'center', width: '100%', gap: 2, py: 0.25, minWidth: 520}}>
      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography
          sx={{
            fontFamily: 'monospace',
            fontSize: 12,
            color: pcolors.textPrimary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {model.id}
        </Typography>
        {model.name && model.name !== model.id && (
          <Typography sx={{fontSize: 11, color: pcolors.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
            {model.name}
          </Typography>
        )}
      </Box>
      <Box sx={{textAlign: 'right', flex: 'none', minWidth: 140}}>
        <Typography sx={{fontSize: 11, fontVariantNumeric: 'tabular-nums', color: pcolors.textSecondary, whiteSpace: 'nowrap'}}>
          {priceLabel(model)}
        </Typography>
        <Typography sx={{fontSize: 10, color: pcolors.textMuted, whiteSpace: 'nowrap'}}>per 1M in / out</Typography>
      </Box>
    </Box>
  );
}

interface BaseProps {
  models: OpenRouterModelEntry[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}

interface SingleProps extends BaseProps {
  multiple?: false;
  value: string;
  onChange: (modelId: string) => void;
}

interface MultiProps extends BaseProps {
  multiple: true;
  value: string[];
  onChange: (modelIds: string[]) => void;
}

export type ModelPickerProps = SingleProps | MultiProps;

export function ModelPicker(props: ModelPickerProps) {
  const byId = new Map(props.models.map(m => [m.id, m]));
  // Synthesize placeholder entries for model ids that aren't (yet) in the
  // catalogue — evolve dialog has hardcoded defaults that may lag behind
  // OpenRouter's live list. Users still see the id and the pricing cells
  // render as "—" rather than crashing the autocomplete.
  const ensureOption = (id: string): OpenRouterModelEntry => {
    const hit = byId.get(id);
    if (hit) return hit;
    return {
      __typename: 'OpenRouterModelEntry',
      id,
      name: id,
      provider: id.split('/')[0] || 'unknown',
      inputPer1M: null,
      outputPer1M: null,
      contextLength: null,
    };
  };

  // Long OpenRouter ids (e.g. `anthropic/claude-opus-4-5-1m-context`) don't
  // fit in the anchor's default width. Give the popper a generous minWidth
  // so the full id + price are always readable, regardless of where the
  // picker is placed on the page.
  const popperSlotProps = {
    popper: {
      style: {minWidth: 560},
      placement: 'bottom-start' as const,
    },
    paper: {sx: {'& .MuiAutocomplete-listbox': {maxHeight: 380, fontSize: 12}}},
  };

  const isKnown = (id: string) => !!id && byId.has(id);
  const warningText = (id: string) =>
    `"${id}" isn't in the OpenRouter catalogue — probably a typo. Likely the provider will return 400. ` +
    `Anthropic ids use dashes not dots (e.g. anthropic/claude-sonnet-4-5).`;

  if (props.multiple) {
    const selected = props.value.map(ensureOption);
    const unknownCount = props.value.filter(id => !isKnown(id)).length;
    const removeAllUnknown = () => {
      props.onChange(props.value.filter(id => isKnown(id)));
    };
    void removeAllUnknown; // surfaced through the TextField helperText action below
    return (
      <Autocomplete
        multiple
        size={props.size || 'small'}
        fullWidth={props.fullWidth ?? true}
        disabled={props.disabled}
        options={props.models}
        value={selected}
        disableCloseOnSelect
        getOptionLabel={m => m.id}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        filterOptions={(opts, state) => {
          const q = state.inputValue.toLowerCase().trim();
          if (!q) return opts;
          return opts.filter(
            m =>
              m.id.toLowerCase().includes(q) ||
              (m.name && m.name.toLowerCase().includes(q)) ||
              m.provider.toLowerCase().includes(q),
          );
        }}
        renderTags={(val, getTagProps) =>
          val.map((m, i) => {
            const known = isKnown(m.id);
            return (
              <Tooltip key={m.id} title={known ? m.id : warningText(m.id)} placement="top">
                <Chip
                  size="small"
                  {...getTagProps({index: i})}
                  icon={known ? undefined : <WarningAmberIcon sx={{fontSize: 14, color: pcolors.amber[700] + ' !important'}} />}
                  label={m.id.split('/').slice(-1)[0]}
                  sx={{
                    fontSize: 11,
                    fontFamily: 'monospace',
                    ...(known
                      ? {}
                      : {
                          bgcolor: pcolors.amber[50],
                          color: pcolors.amber[700],
                          borderColor: pcolors.amber[500],
                          border: `1px solid ${pcolors.amber[500]}`,
                        }),
                  }}
                />
              </Tooltip>
            );
          })
        }
        renderOption={(optProps, m) => (
          <li {...optProps} key={m.id} style={{...optProps.style, paddingLeft: 8, paddingRight: 8}}>
            <ModelOption model={m} />
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label={props.label || 'Models'}
            placeholder={props.placeholder || 'Search…'}
            error={unknownCount > 0}
            helperText={
              unknownCount > 0 ? (
                <Box component="span" sx={{display: 'inline-flex', gap: 0.5, alignItems: 'center'}}>
                  <span>
                    {unknownCount} unknown model id{unknownCount === 1 ? '' : 's'} — will 400 on submit.
                  </span>
                  <Box
                    component="span"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeAllUnknown();
                    }}
                    sx={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: pcolors.blue[700],
                      '&:hover': {color: pcolors.blue[900]},
                    }}
                  >
                    Remove {unknownCount === 1 ? 'it' : 'them all'}
                  </Box>
                </Box>
              ) : undefined
            }
          />
        )}
        onChange={(_, val) => props.onChange(val.map(v => v.id))}
        slotProps={popperSlotProps}
      />
    );
  }

  const current = ensureOption(props.value);
  const currentKnown = isKnown(props.value);
  return (
    <Autocomplete
      size={props.size || 'small'}
      fullWidth={props.fullWidth ?? true}
      disabled={props.disabled}
      options={props.models}
      value={current}
      getOptionLabel={m => m.id}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(opts, state) => {
        const q = state.inputValue.toLowerCase().trim();
        if (!q) return opts;
        return opts.filter(
          m =>
            m.id.toLowerCase().includes(q) ||
            (m.name && m.name.toLowerCase().includes(q)) ||
            m.provider.toLowerCase().includes(q),
        );
      }}
      renderOption={(optProps, m) => (
        <li {...optProps} key={m.id} style={{...optProps.style, paddingLeft: 8, paddingRight: 8}}>
          <ModelOption model={m} />
        </li>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={props.label || 'Model'}
          placeholder={props.placeholder || 'Search…'}
          error={!currentKnown && !!props.value}
          helperText={
            !currentKnown && props.value
              ? warningText(props.value)
              : current
                ? priceLabel(current) + ' per 1M in / out'
                : undefined
          }
        />
      )}
      onChange={(_, val) => {
        if (val) props.onChange(val.id);
      }}
      disableClearable
      slotProps={popperSlotProps}
    />
  );
}
